import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import Title from '../../components/Title';
import SmallButton from '../../components/SmallButton';
import SubmitButton from '../../components/SubmitButton';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import {
  AdminOrderRow,
  AdminOrdersResponse,
  AdminOrderAssignmentResponse,
  AvailableCouriersResponse,
  WarehousesResponse,
  OrderAssignmentResponse,
  PartnerUser,
  Warehouse,
} from '../../models/APIModels';
import errorMessage from '../../services/errorMessage';
import ReactDOM from 'react-dom';
import { Formik } from 'formik';

const PAGE_SIZE = 15;

function todayISO(): string {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

function getCourierDisplayName(courier: PartnerUser): string {
  if (courier.firstName || courier.lastName) {
    return [courier.firstName, courier.lastName].filter(Boolean).join(' ');
  }
  return courier.name || courier.email || `Courier #${courier.id}`;
}

function getCourierPhone(courier: PartnerUser): string | undefined {
  return courier.phoneNumber || courier.phone;
}

// Generate 15-minute interval time slots for 24 hours (00:00 – 23:45)
const ETA_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    ETA_OPTIONS.push(
      `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    );
  }
}

function LogisticsOrdersComponent({ history }: RouteComponentProps) {
  const [orders, setOrders] = useState<AdminOrderRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [networkError, setNetworkError] = useState<string>();
  const [fetching, setFetching] = useState(true);

  // Filters — default fromDate to today so we don't load every order ever
  const [fromDate, setFromDate] = useState(todayISO);
  const [toDate, setToDate] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Modals
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderRow | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Partner options derived from results
  const [partnerOptions, setPartnerOptions] = useState<{ id: number; name: string }[]>([]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Fetch orders — manage loading state ourselves for immediate feedback
  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    setNetworkError(undefined);

    const params: any = { page, pageSize: PAGE_SIZE };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (partnerFilter) params.partnerId = Number(partnerFilter);
    if (typeFilter) params.type = typeFilter;

    axiosKK
      .get<AdminOrdersResponse>('/logistics/admin/orders', { params })
      .then(({ data }) => {
        if (cancelled) return;
        setOrders(data.orders);
        setTotalCount(data.totalCount);

        // Build unique partner options from results
        const partners = new Map<number, string>();
        data.orders.forEach((o) => {
          if (o.partnerId && o.partnerName) {
            partners.set(o.partnerId, o.partnerName);
          }
        });
        setPartnerOptions((prev) => {
          const merged = new Map<number, string>();
          prev.forEach((p) => merged.set(p.id, p.name));
          partners.forEach((name, id) => merged.set(id, name));
          return Array.from(merged, ([id, name]) => ({ id, name }));
        });
      })
      .catch((error) => {
        if (!cancelled) errorMessage(setNetworkError)(error);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => { cancelled = true; };
  }, [page, fromDate, toDate, partnerFilter, typeFilter]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset page when filters change
  const handleFilterChange = (setter: (v: string) => void) => (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleUpdateOrder = (updated: AdminOrderRow) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id && o.type === updated.type ? updated : o)));
  };

  const showInitialLoading = fetching && orders.length === 0;
  const showEmpty = !fetching && orders.length === 0;

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <i className="fas fa-arrow-circle-left" />
          <SmallButton onClick={() => history.push('/admin/dashboard')}>
            Back
          </SmallButton>
        </div>
      </section>

      <Title>Logistics Orders</Title>

      {networkError && <p className="error">{networkError}</p>}

      <section className="admin-logistics-orders">
        <div className="logistics-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>From</label>
              <input
                type="date"
                value={fromDate}
                onChange={handleFilterChange(setFromDate)}
              />
            </div>
            <div className="filter-item">
              <label>To</label>
              <input
                type="date"
                value={toDate}
                onChange={handleFilterChange(setToDate)}
              />
            </div>
            <div className="filter-item">
              <label>Partner</label>
              <select
                value={partnerFilter}
                onChange={handleFilterChange(setPartnerFilter)}
              >
                <option value="">All Partners</option>
                {partnerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Type</label>
              <select
                value={typeFilter}
                onChange={handleFilterChange(setTypeFilter)}
              >
                <option value="">All</option>
                <option value="collection">Collection</option>
                <option value="return">Return</option>
              </select>
            </div>
          </div>
        </div>

        <div className="logistics-table-wrapper">
          {fetching && orders.length > 0 && (
            <div className="logistics-loading-overlay">
              <div className="logistics-loading-spinner" />
            </div>
          )}

          <div className="table">
            <div className="thead">
              <div className="tr">
                <div className="td logistics-col-id">ID</div>
                <div className="td logistics-col-type">Type</div>
                <div className="td logistics-col-date">Date</div>
                <div className="td logistics-col-customer">Customer</div>
                <div className="td logistics-col-address">Address</div>
                <div className="td logistics-col-boxes">Boxes</div>
                <div className="td logistics-col-partner">Partner</div>
                <div className="td logistics-col-courier">Collection Courier</div>
                <div className="td logistics-col-courier">Delivery Courier</div>
                <div className="td logistics-col-warehouse">Warehouse</div>
                <div className="td logistics-col-eta">Collection ETA</div>
                <div className="td logistics-col-eta">Delivery ETA</div>
                <div className="td logistics-col-timeslot">Timeslot</div>
                <div className="td logistics-col-status">Status</div>
                <div className="td logistics-col-actions">Actions</div>
              </div>
            </div>
            <div className="tbody">
              {showInitialLoading ? (
                <div className="tr logistics-placeholder-row">
                  <div className="td" style={{ width: '100%', textAlign: 'center', padding: '40px 20px' }}>
                    <div className="logistics-loading-spinner" style={{ margin: '0 auto 12px' }} />
                    Loading orders...
                  </div>
                </div>
              ) : showEmpty ? (
                <div className="tr logistics-placeholder-row">
                  <div className="td" style={{ width: '100%', textAlign: 'center', padding: '40px 20px' }}>
                    No orders found
                  </div>
                </div>
              ) : (
                orders.map((order) => (
                  <div className="tr" key={`${order.id}-${order.type}`}>
                    <span className="title-label">ID</span>
                    <div className="td logistics-col-id logistics-order-id">#{order.id}</div>

                    <span className="title-label">Type</span>
                    <div className="td logistics-col-type">
                      <span className={`logistics-type-badge logistics-type-badge--${order.type}`}>
                        {order.type}
                      </span>
                    </div>

                    <span className="title-label">Date</span>
                    <div className="td logistics-col-date">{order.date}</div>

                    <span className="title-label">Customer</span>
                    <div className="td logistics-col-customer">{order.customer}</div>

                    <span className="title-label">Address</span>
                    <div className="td logistics-col-address">
                      {order.address}, {order.postcode}
                    </div>

                    <span className="title-label">Boxes</span>
                    <div className="td logistics-col-boxes">{order.boxes}</div>

                    <span className="title-label">Partner</span>
                    <div className="td logistics-col-partner">
                      {order.partnerName || '—'}
                    </div>

                    <span className="title-label">Collection Courier</span>
                    <div className="td logistics-col-courier">
                      {order.collectionDriverName || '—'}
                    </div>

                    <span className="title-label">Delivery Courier</span>
                    <div className="td logistics-col-courier">
                      {order.deliveryDriverName || '—'}
                    </div>

                    <span className="title-label">Warehouse</span>
                    <div className="td logistics-col-warehouse">
                      {order.warehouseName || '—'}
                    </div>

                    <span className="title-label">Collection ETA</span>
                    <div className="td logistics-col-eta logistics-eta-cell">
                      {order.collectionEta || '—'}
                    </div>

                    <span className="title-label">Delivery ETA</span>
                    <div className="td logistics-col-eta logistics-eta-cell">
                      {order.deliveryEta || '—'}
                    </div>

                    <span className="title-label">Timeslot</span>
                    <div className="td logistics-col-timeslot">
                      {order.timeslot || '—'}
                    </div>

                    <span className="title-label">Status</span>
                    <div className="td logistics-col-status">
                      {order.routeLocked && (
                        <span className="logistics-badge logistics-badge--locked">
                          <i className="fas fa-lock" /> Locked
                        </span>
                      )}
                      {order.doorstep && (
                        <span className="logistics-badge logistics-badge--doorstep">
                          Doorstep
                        </span>
                      )}
                      {order.lastScan && (
                        <span className="logistics-badge logistics-badge--scan">
                          {order.lastScan}
                        </span>
                      )}
                      {!order.routeLocked && !order.doorstep && !order.lastScan && '—'}
                    </div>

                    <span className="title-label">Actions</span>
                    <div className="td logistics-col-actions">
                      <button
                        className="logistics-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setShowAssignModal(true);
                        }}
                      >
                        <i className="fas fa-user-plus" /> Assign
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="logistics-pagination">
          <button
            className="logistics-pagination__btn"
            disabled={page <= 1 || fetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            &#8249;
          </button>
          <span className="logistics-pagination__info">
            {fetching && orders.length > 0
              ? 'Loading...'
              : `Page ${page} of ${totalPages || 1}`}
          </span>
          <button
            className="logistics-pagination__btn"
            disabled={page >= totalPages || fetching}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            &#8250;
          </button>
        </div>
      </section>

      {/* Assignment Modal */}
      {selectedOrder && (
        <AssignmentModal
          show={showAssignModal}
          order={selectedOrder}
          close={() => {
            setShowAssignModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={handleUpdateOrder}
        />
      )}
    </>
  );
}

// ── Assignment Modal ───────────────────────────────────────

interface AssignmentModalProps {
  show: boolean;
  order: AdminOrderRow;
  close: () => void;
  onSuccess: (updated: AdminOrderRow) => void;
}

interface AssignmentFormValues {
  collectionCourierId: number | '';
  deliveryCourierId: number | '';
  partnerId: number | '';
  warehouseId: number | '';
  collectionEta: string;
  deliveryEta: string;
}

function AssignmentModal({ show, order, close, onSuccess }: AssignmentModalProps) {
  const [couriers, setCouriers] = useState<PartnerUser[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [networkError, setNetworkError] = useState<string>();
  const [loadingData, setLoadingData] = useState(false);
  const [initialValues, setInitialValues] = useState<AssignmentFormValues>({
    collectionCourierId: '',
    deliveryCourierId: '',
    partnerId: order.partnerId || '',
    warehouseId: '',
    collectionEta: order.collectionEta || '',
    deliveryEta: order.deliveryEta || '',
  });

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingData(true);
    setNetworkError(undefined);

    Promise.all([
      axiosKK.get<AvailableCouriersResponse>('/admin/logistics/couriers'),
      axiosKK.get<WarehousesResponse>('/admin/logistics/warehouses'),
      axiosKK.get<OrderAssignmentResponse>(
        `/admin/logistics/orders/${order.id}/assignment`
      ),
    ])
      .then(([couriersRes, warehousesRes, assignmentRes]) => {
        if (cancelled) return;
        setCouriers(couriersRes.data.couriers || []);
        setWarehouses(warehousesRes.data.warehouses || []);

        const assignment = assignmentRes.data;
        const collectionCourier = assignment.collectionCourier ?? assignment.courier;
        const deliveryCourier = assignment.deliveryCourier ?? assignment.courier;

        setInitialValues({
          collectionCourierId: collectionCourier?.id || '',
          deliveryCourierId: deliveryCourier?.id || '',
          partnerId: order.partnerId || '',
          warehouseId: assignment.warehouse?.id || '',
          collectionEta: order.collectionEta || '',
          deliveryEta: order.deliveryEta || '',
        });
      })
      .catch((error) => {
        if (!cancelled) errorMessage(setNetworkError)(error);
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => { cancelled = true; };
  }, [show, order.id, order.partnerId, order.collectionEta, order.deliveryEta]);

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, close]);

  // Build unique partner options from couriers
  const partnerOptions = React.useMemo(() => {
    const map = new Map<number, string>();
    couriers.forEach((c) => {
      if (c.partnerId) {
        const name = c.name || `Partner #${c.partnerId}`;
        if (!map.has(c.partnerId)) map.set(c.partnerId, name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [couriers]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container logistics-modal__container--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            setNetworkError(undefined);

            const payload: any = {};
            if (values.collectionCourierId) payload.collectionCourierId = Number(values.collectionCourierId);
            if (values.deliveryCourierId) payload.deliveryCourierId = Number(values.deliveryCourierId);
            if (values.partnerId) payload.partnerId = Number(values.partnerId);
            if (values.warehouseId) payload.warehouseId = Number(values.warehouseId);
            if (values.collectionEta) payload.collectionEta = values.collectionEta;
            if (values.deliveryEta) payload.deliveryEta = values.deliveryEta;

            axiosKK
              .put<AdminOrderAssignmentResponse>(
                `/logistics/admin/orders/${order.id}/assignment`,
                payload
              )
              .then(({ data }) => {
                onSuccess(data.order);
                close();
              })
              .catch((error) => {
                errorMessage(setNetworkError)(error);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({ values, isSubmitting, handleChange, handleSubmit, isValid, dirty }) => (
            <form
              onSubmit={handleSubmit}
              className="logistics-modal__form"
              name="logistics-assign-form"
            >
              <div className="logistics-modal__header">
                <div className="logistics-modal__header-info">
                  <h3>Assign Order</h3>
                  <span className="logistics-modal__subtitle">
                    Order #{order.id} &middot; {order.customer}
                  </span>
                </div>
                <button className="logistics-modal__close" type="button" onClick={close}>
                  &times;
                </button>
              </div>

              <div className="logistics-modal__body">
                {loadingData ? (
                  <div className="logistics-modal__loading">
                    Loading assignment data...
                  </div>
                ) : (
                  <>
                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Couriers</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="collectionCourierId">Collection Courier</Label>
                        <select
                          className="logistics-modal__select"
                          id="collectionCourierId"
                          name="collectionCourierId"
                          value={values.collectionCourierId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {couriers.map((courier) => {
                            const displayName = getCourierDisplayName(courier);
                            const phone = getCourierPhone(courier);
                            return (
                              <option key={courier.id} value={courier.id}>
                                {displayName}
                                {phone ? ` (${phone})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="deliveryCourierId">Delivery Courier</Label>
                        <select
                          className="logistics-modal__select"
                          id="deliveryCourierId"
                          name="deliveryCourierId"
                          value={values.deliveryCourierId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {couriers.map((courier) => {
                            const displayName = getCourierDisplayName(courier);
                            const phone = getCourierPhone(courier);
                            return (
                              <option key={courier.id} value={courier.id}>
                                {displayName}
                                {phone ? ` (${phone})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Partner</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="partnerId">Assign Partner</Label>
                        <select
                          className="logistics-modal__select"
                          id="partnerId"
                          name="partnerId"
                          value={values.partnerId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {partnerOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Warehouse</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="warehouseId">Assign Warehouse</Label>
                        <select
                          className="logistics-modal__select"
                          id="warehouseId"
                          name="warehouseId"
                          value={values.warehouseId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {warehouses.map((wh) => {
                            const location = [wh.city, wh.postcode].filter(Boolean).join(', ');
                            return (
                              <option key={wh.id} value={wh.id}>
                                {wh.name}{location ? ` - ${location}` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">ETAs</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="collectionEta">Collection ETA</Label>
                        <select
                          className="logistics-modal__select"
                          id="collectionEta"
                          name="collectionEta"
                          value={values.collectionEta}
                          onChange={handleChange}
                        >
                          <option value="">— No ETA —</option>
                          {ETA_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="deliveryEta">Delivery ETA</Label>
                        <select
                          className="logistics-modal__select"
                          id="deliveryEta"
                          name="deliveryEta"
                          value={values.deliveryEta}
                          onChange={handleChange}
                        >
                          <option value="">— No ETA —</option>
                          {ETA_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {networkError && <span className="error">{networkError}</span>}
              </div>

              <div className="logistics-modal__footer">
                <SubmitButton
                  disabled={loadingData || !isValid || !dirty}
                  isSubmitting={isSubmitting}
                >
                  Save Assignment
                </SubmitButton>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>,
    document.body
  );
}

export default withRouter(LogisticsOrdersComponent as any);
